import { WalletError, type ApiClient } from '@cef-ai/wallet-api-client';
import type { Identity } from '@cef-ai/wallet-identity';
import type { HostToPopup, PopupToHost } from '@cef-ai/wallet';
import type { AppContextStore, DelegationsStore, PendingConsentStore } from '~/stores';
import { isFirstPartyOrigin } from './sameSite';

export interface RequestDelegationHandlerDeps {
  identity: Identity;
  apiClient: ApiClient;
  appContextStore: AppContextStore;
  delegationsStore: DelegationsStore;
  pendingConsentStore: PendingConsentStore;
  /**
   * The wallet SPA's own origin, used to decide whether a request's
   * browser-verified origin is first-party (same-site). Bridges default this
   * to `globalThis.location.origin` (the origin this popup/iframe is served
   * from); tests inject an explicit value. See `isFirstPartyOrigin`.
   */
  walletOrigin: string;
  /**
   * Whole origins that auto-approve delegation without a prompt — explicit
   * per-environment config, never derived from the wallet's own hostname
   * (CEF-AI/wallet#74). Absent means only the wallet's own origin qualifies,
   * so every consumer is prompted.
   */
  firstPartyOrigins?: readonly string[];
  send: (msg: PopupToHost) => void;
}

/**
 * Parse a TTL string like "1h", "30m", "60s" into seconds. Spec §4.3 leaves
 * the format open; we accept the common shapes the API will see.
 *
 * Throws on malformed input. The previous silent-return-0 behaviour created
 * zero-second delegations that the API would either reject server-side or
 * mint as immediately-expired tokens — both worse than a typed error here.
 *
 * Also rejects values that exceed the maximum safe representable seconds
 * (~285 million years) to avoid silently overflowing into a non-integer.
 */
const MAX_TTL_SECONDS = Number.MAX_SAFE_INTEGER;
function parseTtl(ttl: string): number {
  const m = ttl.match(/^(\d+)([smhd])$/);
  if (!m) {
    throw new WalletError('validation', `requestDelegation: invalid ttl "${ttl}" (expected e.g. "1h", "30m", "60s")`);
  }
  const n = Number(m[1]);
  const multiplier = m[2] === 's' ? 1 : m[2] === 'm' ? 60 : m[2] === 'h' ? 3600 : 86400;
  const seconds = n * multiplier;
  if (!Number.isFinite(seconds) || seconds > MAX_TTL_SECONDS) {
    throw new WalletError('validation', `requestDelegation: ttl "${ttl}" overflows safe integer range`);
  }
  return seconds;
}

export function createRequestDelegationHandler(deps: RequestDelegationHandlerDeps) {
  return async (
    msg: Extract<HostToPopup, { type: 'wallet:requestDelegation' }>,
    meta: { origin: string },
  ): Promise<void> => {
    if (!deps.identity.isAuthenticated) {
      throw new WalletError('unauthorized', 'requestDelegation: not logged in');
    }
    const ttlSeconds = parseTtl(msg.ttl);

    // First-party default: when the request's BROWSER-VERIFIED origin
    // (`meta.origin`, i.e. `MessageEvent.origin`, which the bridge already
    // validated against the expected host) is one of this environment's
    // configured first-party origins, consent defaults to `approve-always` and
    // we mint WITHOUT a prompt — even on the first request. Per the spec,
    // connecting a first-party app IS the explicit grant. Every other origin
    // falls through to the approval/prompt policy below.
    //
    // "First-party" is a CONFIGURED LIST, not a same-site inference from the
    // wallet's hostname — see `isFirstPartyOrigin` and CEF-AI/wallet#74. The
    // decision keys ONLY on the verified origin, never the self-declared
    // `?origin=`/`appContext.origin`. Capabilities and TTL are NOT broadened
    // here — `approve-always` only removes the re-prompt for the already-scoped
    // request.
    const firstParty = isFirstPartyOrigin(meta.origin, deps.walletOrigin, deps.firstPartyOrigins);

    // Spec §3: if a matching auto-approve rule already exists, skip the prompt.
    const approval = firstParty
      ? null
      : deps.delegationsStore.findApproval({
          hostOrigin: meta.origin,
          capabilities: msg.scope.capabilities,
          ttlCapSeconds: ttlSeconds,
          agentPubkey: msg.scope.agentPubkey,
        });

    // Cycle 7: when there's no stored approval, ask the user via the in-popup
    // consent prompt instead of throwing `user-cancelled`. The handler awaits
    // a decision; React renders `<ConsentPrompt>` and calls `resolveCurrent()`.
    // First-party requests skip this block entirely (mint straight through).
    if (!firstParty && !approval) {
      const decision = await deps.pendingConsentStore.request({
        kind: 'delegation',
        hostOrigin: meta.origin,
        capabilities: msg.scope.capabilities,
        ttlSeconds,
        agentPubkey: msg.scope.agentPubkey,
      });
      if (decision.kind === 'deny') {
        throw new WalletError('user-cancelled', 'requestDelegation: user denied the request');
      }
      if (decision.kind === 'approve-always') {
        deps.delegationsStore.saveApproval({
          hostOrigin: meta.origin,
          capabilities: msg.scope.capabilities,
          ttlCapSeconds: ttlSeconds,
          agentPubkey: msg.scope.agentPubkey,
        });
      }
      // 'approve-once' falls through without persisting a rule.
    }

    const result = await deps.apiClient.delegations.create({
      scope: {
        capabilities: msg.scope.capabilities,
        appId: msg.scope.appId ?? deps.appContextStore.app.appId,
        agentPubkey: msg.scope.agentPubkey,
        constraints: msg.scope.constraints,
      },
      ttl: msg.ttl,
    });

    deps.delegationsStore.recordIssued({
      token: result.token,
      expiresAtMs: Date.parse(result.expiresAt),
      hostOrigin: meta.origin,
      capabilities: msg.scope.capabilities,
      issuer: result.issuer,
    });

    deps.send({ type: 'wallet:result', id: msg.id, result });
  };
}
